import React, { useEffect, useState } from 'react';
import { COLORS } from '../App';

const API_BASE = window.location.port === '3000'
  ? 'http://localhost:4100/api'
  : window.location.pathname.startsWith('/cmbs')
    ? '/cmbs/api'
    : '/api';

const ROW_DEFS = [
  { key: 'effective_gross_income', label: 'Effective Gross Income', fmt: 'currency' },
  { key: 'operating_expenses', label: 'Operating Expenses', fmt: 'currency', expense: true },
  { key: 'net_operating_income', label: 'Net Operating Income (NOI)', fmt: 'currency', emphasis: true },
  { key: 'capital_reserves', label: 'Capital Reserves', fmt: 'currency', expense: true },
  { key: 'net_cash_flow', label: 'Net Cash Flow (NCF)', fmt: 'currency', emphasis: true },
  { key: 'cap_rate', label: 'Cap Rate', fmt: 'percent', expense: true },
  { key: 'implied_value', label: 'Implied Value', fmt: 'currency' },
  { key: 'loan_amount', label: 'Loan Amount', fmt: 'currency', noDelta: true },
  { key: 'ltv', label: 'LTV', fmt: 'percent', noDelta: true },
  { key: 'dscr', label: 'DSCR', fmt: 'ratio', emphasis: true },
  { key: 'debt_yield', label: 'Debt Yield', fmt: 'percent', emphasis: true },
];

function fmtVal(val, fmt) {
  if (val == null || isNaN(val)) return '—';
  if (fmt === 'currency') return '$' + Math.round(val).toLocaleString();
  if (fmt === 'percent') return val.toFixed(2) + '%';
  if (fmt === 'ratio') return val.toFixed(2) + 'x';
  return String(val);
}

function computeDelta(a, b, fmt) {
  if (a == null || b == null || isNaN(a) || isNaN(b) || b === 0) return null;
  if (fmt === 'percent') {
    const bps = (a - b) * 100;
    return { value: bps, display: (bps >= 0 ? '+' : '') + Math.round(bps) + ' bps' };
  }
  if (fmt === 'ratio') {
    const diff = a - b;
    return { value: diff, display: (diff >= 0 ? '+' : '') + diff.toFixed(2) + 'x' };
  }
  const pct = ((a - b) / Math.abs(b)) * 100;
  return { value: pct, display: (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%' };
}

function deltaStyle(delta, expense, threshold) {
  const base = { fontFamily: "'SF Mono', Monaco, Consolas, monospace", fontSize: 12, fontWeight: 600, padding: '9px 12px', textAlign: 'right' };
  if (!delta || Math.abs(delta.value) < threshold) return { ...base, color: '#6b7280' };
  const conservative = expense ? delta.value > 0 : delta.value < 0;
  return conservative
    ? { ...base, color: '#059669', background: '#ecfdf5' }
    : { ...base, color: '#dc2626', background: '#fef2f2' };
}

function InfoBanner({ children }) {
  return (
    <div style={{ padding: '12px 16px', background: '#f9fafb', color: '#6b7280', borderRadius: 8, fontSize: 13, marginBottom: 12, border: '1px solid #e5e7eb' }}>
      {children}
    </div>
  );
}

export default function UWComparison({ deal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('cmbs_token');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/underwriting/${deal.id}/comparison`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(json => { if (!cancelled) setData(json); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [deal.id]);

  if (loading) return <InfoBanner>Loading underwriting comparison...</InfoBanner>;
  if (error) return <InfoBanner style={{ color: COLORS.danger }}>Could not load comparison: {error}</InfoBanner>;
  if (!data) return null;

  const { broker, lender, fitch, lender_flags, fitch_flags } = data;
  const hasBroker = broker && Object.values(broker).some(v => v != null && typeof v === 'number');
  const hasLender = lender != null;
  const hasFitch = fitch != null;

  const thStyle = {
    padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontSize: 11,
    textTransform: 'uppercase', letterSpacing: '0.3px',
    borderBottom: `3px solid ${COLORS.gold}`, color: '#fff',
  };
  const tdNumStyle = {
    padding: '9px 12px', textAlign: 'right', borderBottom: '1px solid #f1f1f1',
    fontFamily: "'SF Mono', Monaco, Consolas, monospace", fontSize: 13, color: COLORS.navy,
    fontVariantNumeric: 'tabular-nums',
  };
  const tdLabelStyle = {
    padding: '9px 12px', textAlign: 'left', borderBottom: '1px solid #f1f1f1',
    color: COLORS.navy, fontWeight: 500, fontSize: 13,
  };

  const FLAG_COLORS = {
    critical: { bg: '#fef2f2', badge: '#dc2626' },
    warning: { bg: '#fffbeb', badge: '#d97706' },
    info: { bg: '#eff6ff', badge: '#3b82f6' },
  };

  return (
    <div style={{ paddingTop: 4 }}>
      <div style={{ marginBottom: 18 }}>
        <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, margin: '0 0 6px' }}>Underwriting Comparison</h3>
        <p style={{ color: COLORS.textLight, fontSize: 13, margin: 0, maxWidth: 760 }}>
          Side-by-side cash flow comparison: Broker (extracted from sizer) vs. Lender Standard vs. Fitch Conservative.
          Conservative-direction deltas are green; aggressive deltas are red; immaterial deltas are gray.
        </p>
      </div>

      {!hasBroker && <InfoBanner>No broker numbers available. Run extraction on the broker sizer to populate the Broker column.</InfoBanner>}
      {!hasLender && <InfoBanner>No Lender Standard run yet. Run Lender UW from the Assumptions tab to populate the Lender column.</InfoBanner>}
      {!hasFitch && <InfoBanner>No Fitch Conservative run yet. Run Fitch Stress from the Assumptions tab to populate the Fitch column.</InfoBanner>}

      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: COLORS.navy }}>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left' }}>Line Item</th>
              <th style={thStyle}>Broker</th>
              <th style={thStyle}>Lender Standard</th>
              <th style={thStyle}>Fitch Conservative</th>
              <th style={thStyle}>Lender vs. Broker</th>
              <th style={thStyle}>Fitch vs. Lender</th>
            </tr>
          </thead>
          <tbody>
            {ROW_DEFS.map(row => {
              const bVal = broker?.[row.key];
              const lVal = lender?.[row.key];
              const fVal = fitch?.[row.key];

              const lvb = !row.noDelta ? computeDelta(lVal, bVal, row.fmt) : null;
              const fvl = !row.noDelta ? computeDelta(fVal, lVal, row.fmt) : null;
              const threshold = row.fmt === 'percent' ? 25 : (row.fmt === 'ratio' ? 0.05 : 5);

              const rowBg = row.emphasis ? '#fffbf0' : 'transparent';
              const labelWeight = row.emphasis ? 700 : 500;
              const numWeight = row.emphasis ? 700 : 400;

              return (
                <tr key={row.key} style={{ background: rowBg }}>
                  <td style={{ ...tdLabelStyle, fontWeight: labelWeight, background: rowBg }}>{row.label}</td>
                  <td style={{ ...tdNumStyle, fontWeight: numWeight, background: rowBg }}>{fmtVal(bVal, row.fmt)}</td>
                  <td style={{ ...tdNumStyle, fontWeight: numWeight, background: rowBg }}>{fmtVal(lVal, row.fmt)}</td>
                  <td style={{ ...tdNumStyle, fontWeight: numWeight, background: rowBg }}>{fmtVal(fVal, row.fmt)}</td>
                  <td style={deltaStyle(lvb, row.expense, threshold)}>{lvb ? lvb.display : '—'}</td>
                  <td style={deltaStyle(fvl, row.expense, threshold)}>{fvl ? fvl.display : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(lender_flags.length > 0 || fitch_flags.length > 0) && (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: 16, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 12px', color: COLORS.navy, fontSize: 14, fontFamily: 'Georgia, serif' }}>Threshold Flags</h4>
          {[
            { label: 'Lender Standard', flags: lender_flags },
            { label: 'Fitch Conservative', flags: fitch_flags },
          ].filter(g => g.flags.length > 0).map(group => (
            <div key={group.label} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>
                {group.label}
              </div>
              {group.flags.map((f, i) => {
                const fc = FLAG_COLORS[f.level] || FLAG_COLORS.info;
                return (
                  <div key={i} style={{ background: fc.bg, borderRadius: 6, padding: '8px 12px', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, letterSpacing: '0.3px', textTransform: 'uppercase', background: fc.badge, color: '#fff', flexShrink: 0, marginTop: 1 }}>
                      {f.level}
                    </span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{f.text}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div style={{ textAlign: 'right', color: '#9ca3af', fontSize: 11 }}>
        Lender: most recent Lender Standard run{lender?.run_at ? ` (${new Date(lender.run_at).toLocaleString()})` : ' — none yet'}.{' '}
        Fitch: most recent Fitch Conservative run{fitch?.run_at ? ` (${new Date(fitch.run_at).toLocaleString()})` : ' — none yet'}.
      </div>
    </div>
  );
}
